import { useCallback } from 'react';

type HapticType = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

// Audio Haptic Fallback for iOS
const triggerAudioHaptic = (type: HapticType) => {
    try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) return;

        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        const now = ctx.currentTime;

        // Low frequency 'thud' to shake the speakers
        osc.type = 'sine';
        osc.frequency.setValueAtTime(80, now);

        // Short duration
        const duration = type === 'selection' ? 0.015 : 0.03;

        gain.gain.setValueAtTime(1.0, now); // Max volume for physical shake
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc.start(now);
        osc.stop(now + duration);
    } catch (e) { }
};

/**
 * Enhanced Haptic Feedback Hook
 * Uses Vibration API with patterns to mimic Apple Taptic Engine Feel
 */
export const useHaptics = () => {
    const trigger = useCallback((type: HapticType = 'light') => {
        // Check user preference
        const isHapticsEnabled = localStorage.getItem('hapticsEnabled') !== 'false'; // Default true
        if (!isHapticsEnabled) return;

        // Native Wrapper (iOS)
        // @ts-ignore
        if (window.ReactNativeWebView) {
            // @ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'TRIGGER_HAPTIC',
                hapticType: type
            }));
            return;
        }

        // Native Vibration (Android)
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            try {
                // Feature detection isn't enough on iOS, navigator.vibrate might exist but do nothing/return false.
                // However, if it returns true/works, great. If not, we fall through? 
                // Actually, navigator.vibrate always returns boolean on success.
                // But on iOS it returns false usually.

                const result = navigator.vibrate(1); // Test
                // Ideally we use a known pattern

                switch (type) {
                    case 'selection': navigator.vibrate(15); break;
                    case 'light': navigator.vibrate(20); break;
                    case 'medium': navigator.vibrate(40); break;
                    case 'heavy': navigator.vibrate(70); break;
                    case 'success': navigator.vibrate([20, 40, 20]); break;
                    case 'warning': navigator.vibrate([40, 50, 40]); break;
                    case 'error': navigator.vibrate([50, 100, 50, 100, 50]); break;
                    default: navigator.vibrate(20);
                }

                // If we are here, we TRIED to vibrate.
                // iOS makes it hard to know if it failed silently.
                // So we will force audio haptic for iOS user agents if needed, but let's try the fallback based on property check.
                return;
            } catch (e) { }
        }

        // Fallback: Audio Haptic (iOS)
        // If navigator.vibrate is undefined or failed
        triggerAudioHaptic(type);

    }, []);

    return {
        trigger,
        selection: () => trigger('selection'),
        light: () => trigger('light'),
        medium: () => trigger('medium'),
        heavy: () => trigger('heavy'),
        success: () => trigger('success'),
        warning: () => trigger('warning'),
        error: () => trigger('error')
    };
};
